// Dr.Rx™ Custom Drug Monograph Addition Modal
import React, { useState, useEffect } from 'react';
import type { DrugInfo } from '../types';
import { getDrugInfoFromGeminiForModal } from '../services/geminiService';
import { addUserDrug } from '../services/pharmacyService';
import { useAuth } from '../contexts/AuthContext';

const BrainIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.871 14.129c-1.994-1.994-1.994-5.229 0-7.223s5.229-1.994 7.223 0c1.994 1.994 1.994 5.229 0 7.223-1.994 1.994-5.229 1.994-7.223 0zM12 12v.01M12 4.5V2m0 20v-2.5m-7-13l-1.5-1.5m1.5 1.5l-1.5 1.5m14.5-1.5l1.5-1.5m-1.5 1.5l1.5 1.5M4.5 12H2m20 0h-2.5" /></svg>;
const CheckIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;

interface AddDrugModalProps {
    drugName: string;
    onClose: () => void;
    onSave?: (newDrug: DrugInfo) => void;
    onDrugAdded?: () => void;
}

const FormInput: React.FC<{ label: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; name: string; }> = ({ label, value, onChange, name }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-400 mb-1">{label}</label>
        <input type="text" id={name} name={name} value={value} onChange={onChange} className="w-full bg-[#101D35] border border-[#3A4A67] rounded-lg p-2.5 focus:outline-none focus:ring-1 focus:ring-yellow-400" />
    </div>
);

const FormTextArea: React.FC<{ label: string; value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; name: string; rows?: number }> = ({ label, value, onChange, name, rows = 3 }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-400 mb-1">{label}</label>
        <textarea id={name} name={name} value={value} onChange={onChange} rows={rows} className="w-full bg-[#101D35] border border-[#3A4A67] rounded-lg p-2.5 focus:outline-none focus:ring-1 focus:ring-yellow-400 resize-none" />
    </div>
);

const SkeletonLoader: React.FC = () => (
    <div className="space-y-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
            <div key={i}>
                <div className="h-4 bg-gray-700/50 rounded w-1/4 mb-2"></div>
                <div className="h-10 bg-gray-700/50 rounded-lg"></div>
            </div>
        ))}
    </div>
);

const AddDrugModal: React.FC<AddDrugModalProps> = ({ drugName, onClose, onSave, onDrugAdded }) => {
    const { uid } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState<Partial<DrugInfo>>({
        brandName: '',
        genericName: '',
        therapeuticClass: '',
        dosageForm: '',
        pharmacology: '',
        isRx: true,
        isOtc: false,
        hasBlackBoxWarning: false,
    });
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
        const fetchInfo = async () => {
            setIsLoading(true);
            try {
                const info = await getDrugInfoFromGeminiForModal(drugName);
                // Normalize pharmacology to string if it's an object
                const normalizedInfo = {
                    ...info,
                    pharmacology: typeof info.pharmacology === 'object' 
                        ? info.pharmacology.mechanismOfAction 
                        : info.pharmacology
                };
                setFormData(prev => ({ ...prev, ...normalizedInfo }));
            } catch (error) {
                console.error("Failed to fetch drug info for modal:", error);
                setFormData(prev => ({ ...prev, brandName: drugName }));
            } finally {
                setIsLoading(false);
            }
        };
        fetchInfo();
    }, [drugName]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleSave = () => {
        if (!isVerified || !formData.brandName || !formData.genericName || !uid) return;

        const newDrug: DrugInfo = {
            id: `user-${Date.now()}`,
            brandName: formData.brandName,
            genericName: formData.genericName,
            therapeuticClass: formData.therapeuticClass || '',
            dosageForm: formData.dosageForm || '',
            strength: formData.strength || '',
            manufacturer: formData.manufacturer || '',
            therapeuticEquivalence: formData.therapeuticEquivalence || 'N/A',
            isRx: formData.isRx || false,
            isOtc: formData.isOtc || false,
            hasBlackBoxWarning: formData.hasBlackBoxWarning || false,
            blackBoxWarning: formData.blackBoxWarning || '',
            dosingAndAdministration: typeof formData.dosingAndAdministration === 'string' ? formData.dosingAndAdministration : '',
            adverseReactions: typeof formData.adverseReactions === 'string' ? formData.adverseReactions : '',
            contraindications: formData.contraindications || '',
            drugInteractions: formData.drugInteractions || '',
            pharmacology: typeof formData.pharmacology === 'string' ? formData.pharmacology : (formData.pharmacology?.mechanismOfAction || ''),
            isUserAdded: true,
        };
        
        addUserDrug(newDrug, uid);
        if (onSave) onSave(newDrug);
        if (onDrugAdded) onDrugAdded();
    };

    return (
        <div onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 page-enter-active" role="dialog" aria-modal="true">
            <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-[#1a2a47] to-[#15243E] rounded-2xl border border-blue-900/50 w-full max-w-2xl text-gray-300 shadow-2xl flex flex-col max-h-[90vh]">
                <header className="p-6 border-b border-blue-900/50 flex-shrink-0">
                    <h2 className="text-2xl font-bold text-white">Add "{drugName}" to Your Index</h2>
                    <p className="text-gray-400 mt-1">Information below is AI-populated. Please review and verify all fields before saving.</p>
                </header>

                <div className="p-6 overflow-y-auto space-y-4">
                    {isLoading ? <SkeletonLoader /> : (
                        <>
                            <div className="flex items-center gap-2 text-sm text-purple-300 bg-purple-800/50 border border-purple-700/60 px-3 py-2 rounded-lg">
                                <BrainIcon /> <span>Fields pre-populated by Dr.Rx AI. Please verify accuracy.</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormInput label="Brand Name" name="brandName" value={formData.brandName || ''} onChange={handleInputChange} />
                                <FormInput label="Generic Name" name="genericName" value={formData.genericName || ''} onChange={handleInputChange} />
                            </div>
                            <FormInput label="Therapeutic Class" name="therapeuticClass" value={formData.therapeuticClass || ''} onChange={handleInputChange} />
                            <FormInput label="Dosage Form (e.g., Tablet; Oral)" name="dosageForm" value={formData.dosageForm || ''} onChange={handleInputChange} />
                            <FormTextArea 
                                label="Pharmacology (Mechanism of Action)" 
                                name="pharmacology" 
                                value={typeof formData.pharmacology === 'string' ? formData.pharmacology : ''} 
                                onChange={handleInputChange} 
                            />
                            <div className="flex items-center gap-6 pt-4">
                                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="isRx" checked={formData.isRx} onChange={handleCheckboxChange} className="h-4 w-4 rounded" /> Rx</label>
                                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="isOtc" checked={formData.isOtc} onChange={handleCheckboxChange} className="h-4 w-4 rounded" /> OTC</label>
                                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="hasBlackBoxWarning" checked={formData.hasBlackBoxWarning} onChange={handleCheckboxChange} className="h-4 w-4 rounded" /> Has Black Box Warning</label>
                            </div>
                            {formData.hasBlackBoxWarning && (
                                <FormTextArea label="Black Box Warning Text" name="blackBoxWarning" value={formData.blackBoxWarning || ''} onChange={handleInputChange} />
                            )}
                        </>
                    )}
                </div>

                <footer className="p-6 border-t border-blue-900/50 mt-auto space-y-4 flex-shrink-0">
                    <label className="flex items-start gap-3 p-3 bg-red-900/30 rounded-lg cursor-pointer border border-red-800/50">
                        <div className="relative flex items-center mt-0.5">
                            <input type="checkbox" className="peer sr-only" checked={isVerified} onChange={() => setIsVerified(!isVerified)} />
                            <div className="w-6 h-6 bg-[#2c3a53] rounded flex-shrink-0 border border-blue-800/50 peer-checked:bg-blue-600 peer-checked:border-blue-500 transition-all flex items-center justify-center">
                                {isVerified && <CheckIcon />}
                            </div>
                        </div>
                        <span className="text-sm text-red-200/90">I acknowledge that this AI-generated information is for educational purposes and I have independently verified its accuracy before saving.</span>
                    </label>
                    <div className="flex justify-end gap-4">
                        <button onClick={onClose} className="px-6 py-2 rounded-lg text-gray-300 font-semibold hover:bg-white/10 transition-colors">Cancel</button>
                        <button onClick={handleSave} disabled={!isVerified || !formData.brandName || !formData.genericName} className="bg-yellow-400 text-gray-900 font-bold py-2 px-6 rounded-lg hover:bg-yellow-300 transition-transform hover:scale-105 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed disabled:scale-100">Save to Index</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default AddDrugModal;